/**
 * European hops — Germany, Czechia, the UK, France, Poland and Slovenia.
 *
 * Figures follow the grower and merchant specifications published by BarthHaas,
 * Hopsteiner, Hop Products, Charles Faram, Bohemia Hop and the Slovenian
 * Institute of Hop Research as of our knowledge snapshot. Everything is stored
 * as a range because alpha, oil and cohumulone all move with crop year, farm
 * and season; cohumulone is expressed as a percentage *of alpha acid*, which is
 * how the continental spec sheets quote it.
 *
 * Two rows here are `representative` rather than `spec-sheet`, and for one
 * shared reason: BarthHaas publishes their total oil as a ceiling ("up to 3
 * mL/100 g") and no floor, while `hopSchema` wants a range. Agnus and Tango
 * therefore carry a published maximum and a class-typical minimum, and say so
 * by their provenance. Everything else on those rows is BarthHaas's.
 *
 * Some rows are `representative` because their only published figures come from
 * a **retailer** — Yakima Valley Hops, where a lot of homebrewers actually buy.
 * Their per-variety tables are complete and are plainly republished grower data,
 * which is better than nothing and worse than the grower's own sheet, and
 * `spec-sheet` would claim more than we can check. Those rows carry no `url`
 * either: `product-urls.test.ts` blocklists retailer hosts, and rightly, because
 * a shop's product page outlives the variety by about a season.
 *
 * Charles Faram's Hop Oracle is **not** used for oil anywhere in this package.
 * Its total-oil column is an order of magnitude high — its Dana page states
 * 20.4-30.9 mL/100 g against a true figure near 2.0-3.1, and Agnus, Triskel and
 * Styrian Dragon repeat the error. Alpha, beta and cohumulone there cross-check
 * against BarthHaas and the British Hop Association; the oil column does not,
 * and it is the one figure worth refusing outright, because alpha and oil are
 * what a recipe actually calculates with.
 */

import type { Hop } from '../../schema/hop';

/** European hop varieties. @see Hop */
export const europeHops: readonly Hop[] = [
  {
    id: 'admiral',
    name: 'Admiral',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 13, max: 16 },
    betaAcidPercent: { min: 4.5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 36, max: 42 },
    totalOilMlPer100g: { min: 1, max: 1.8 },
    oilComposition: {
      myrcenePercent: 42,
      humulenePercent: 27,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    substitutes: ['target', 'northdown', 'challenger', 'magnum'],
    flavorTags: ['resinous', 'orange-peel', 'herbal', 'bitter'],
    description:
      'A high-alpha Wye variety bred to give British bitters a clean, firm bitterness without shipping American hops in. Best used early in the boil, where its marmalade-and-resin edge stays in the background.',
    tags: ['english', 'uk', 'wye', 'high-alpha'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/admiral',
    provenance: 'spec-sheet',
  },
  {
    id: 'agnus',
    name: 'Agnus',
    origin: 'Czech Republic',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 9, max: 13.5 },
    betaAcidPercent: { min: 4, max: 7 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 2, max: 3 },
    oilComposition: {
      myrcenePercent: 37.5,
      humulenePercent: null,
      caryophyllenePercent: null,
      farnesenePercent: null,
    },
    substitutes: ['premiant', 'magnum', 'northern-brewer', 'taurus'],
    flavorTags: ['spicy', 'herbal', 'woody', 'resinous'],
    description:
      "The Zatec institute's answer to the imported high-alpha hop, registered in 2000 out of Bor, Sladek, Saaz, Northern Brewer and Fuggle stock. It bitters a lager cleanly at Magnum rates while keeping a Saazer spice underneath, which is the whole reason a Czech brewery reaches for it.",
    tags: ['czech', 'zatec', 'high-alpha', 'bittering'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/agnus',
    provenance: 'representative',
  },
  {
    id: 'aramis',
    name: 'Aramis',
    origin: 'France',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 6.5, max: 8.5 },
    betaAcidPercent: { min: 3.5, max: 5 },
    cohumulonePercentOfAlpha: { min: 18, max: 22 },
    totalOilMlPer100g: { min: 1.2, max: 1.8 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 24,
      caryophyllenePercent: 10,
      farnesenePercent: 1,
    },
    substitutes: ['strisselspalt', 'hallertauer-mittelfrueh', 'tettnanger', 'saphir'],
    flavorTags: ['herbal', 'spicy', 'citrus', 'floral'],
    description:
      'An Alsatian cross of Strisselspalt and Whitbread Golding that keeps the delicate herbal lift of its Alsatian parent but carries more alpha. A refined late-addition hop for saisons, bières de garde and continental pale lagers.',
    tags: ['french', 'alsace', 'comptoir-agricole'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/aramis',
    provenance: 'spec-sheet',
  },
  {
    id: 'archer',
    name: 'Archer',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 6 },
    betaAcidPercent: { min: 2, max: 3 },
    cohumulonePercentOfAlpha: { min: 32, max: 38 },
    totalOilMlPer100g: { min: 0.6, max: 0.8 },
    oilComposition: {
      myrcenePercent: 22.5,
      humulenePercent: 28,
      caryophyllenePercent: null,
      farnesenePercent: 0.4,
    },
    substitutes: ['jester', 'olicana', 'harlequin'],
    flavorTags: ['floral', 'lime', 'peach', 'citrus'],
    description:
      'A Charles Faram development-program hop with an unusually delicate profile for a modern English variety: lime zest and white peach over flowers, rather than the tropical push its contemporaries go for. Low oil and low alpha, so it wants a late addition and a light hand.',
    tags: ['english', 'uk', 'charles-faram', 'modern'],
    url: 'https://www.britishhops.org.uk/varieties/archer/',
    provenance: 'spec-sheet',
  },
  {
    id: 'ariana',
    name: 'Ariana',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 8, max: 14.5 },
    betaAcidPercent: { min: 3.7, max: 6.6 },
    cohumulonePercentOfAlpha: { min: 40, max: 42 },
    totalOilMlPer100g: { min: 1.1, max: 2.5 },
    oilComposition: {
      myrcenePercent: 47,
      humulenePercent: 18,
      caryophyllenePercent: 5,
      farnesenePercent: 0.1,
    },
    substitutes: ['hallertau-blanc', 'mandarina-bavaria', 'huell-melon', 'citra'],
    flavorTags: ['passionfruit', 'blackcurrant', 'pineapple', 'berry', 'tropical'],
    description:
      'A Hüll daughter of Herkules, trialled as 2010/72/20 and released in 2016. Passionfruit, blackcurrant and pineapple over berry — Germany’s answer to a modern dual hop. High cohumulone, so bitter with something cleaner and save Ariana for whirlpool and dry hop.',
    tags: ['german', 'hallertau', 'huell', 'flavor-hop', 'dual-purpose'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/ariana',
    provenance: 'spec-sheet',
  },
  {
    id: 'aurora',
    name: 'Aurora',
    origin: 'Slovenia',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 2.7, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 22, max: 26 },
    totalOilMlPer100g: { min: 0.9, max: 1.8 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 22,
      caryophyllenePercent: 7,
      farnesenePercent: 6,
    },
    substitutes: ['styrian-goldings', 'northern-brewer', 'perle', 'celeia'],
    flavorTags: ['floral', 'herbal', 'lime', 'spicy', 'woody'],
    description:
      'Also sold as Super Styrian Aurora, a Northern Brewer daughter from the Slovenian Institute. Floral and herbal with lime and a peppery wood note — the workhorse dual hop of Slovenian brewing, comfortable from bittering through a late addition in lagers and pale ales.',
    tags: ['slovenian', 'ihps', 'super-styrian', 'dual-purpose'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/aurora',
    provenance: 'spec-sheet',
  },
  {
    id: 'barbe-rouge',
    name: 'Barbe Rouge',
    origin: 'France',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 6, max: 9 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 20, max: 26 },
    totalOilMlPer100g: { min: 0.8, max: 1.5 },
    oilComposition: null,
    substitutes: ['strisselspalt', 'mandarina-bavaria', 'huell-melon', 'cascade'],
    flavorTags: ['red-berry', 'strawberry', 'citrus', 'floral'],
    description:
      'A modern Alsatian aroma hop named for the red bracts on its cones, giving soft red-fruit and citrus notes rather than anything resinous. Shines whirlpooled into wheat beers, saisons and pale ales where a berry note is wanted.',
    tags: ['french', 'alsace', 'comptoir-agricole'],
    url: 'https://charlesfaram.com/uk/hop-oracle/barbe-rouge/',
    provenance: 'representative',
  },
  {
    id: 'boadicea',
    name: 'Boadicea',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 3, max: 4 },
    cohumulonePercentOfAlpha: { min: 26, max: 26 },
    totalOilMlPer100g: { min: 1.4, max: 2 },
    oilComposition: {
      myrcenePercent: 33,
      humulenePercent: 20,
      caryophyllenePercent: null,
      farnesenePercent: 5,
    },
    substitutes: ['first-gold', 'progress', 'challenger', 'pilgrim'],
    flavorTags: ['floral', 'spicy', 'grassy', 'orchard-fruit'],
    description:
      "Released from Wye in 2004 as the world's first aphid-resistant hop, which is most of the reason it exists — it is grown with a fraction of the spraying its neighbors need. In the glass it is mild and well mannered: orchard blossom, a little grass, a little spice.",
    tags: ['english', 'uk', 'wye', 'aphid-resistant'],
    url: 'https://www.britishhops.org.uk/varieties/boadicea/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bobek',
    name: 'Bobek',
    origin: 'Slovenia',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3.5, max: 7 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 27, max: 31 },
    totalOilMlPer100g: { min: 0.8, max: 2.5 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 20,
      caryophyllenePercent: 6,
      farnesenePercent: 5,
    },
    substitutes: ['styrian-goldings', 'celeia', 'fuggle', 'saaz'],
    flavorTags: ['pine', 'floral', 'lemon', 'herbal', 'earthy'],
    description:
      'A Slovenian Fuggle-type with more pine and lemon than Styrian Golding, and a generous oil load for its alpha. Traditional late hop for European lagers and English-leaning ales; the oil band is wide because the crop swings hard year to year.',
    tags: ['slovenian', 'ihps', 'fuggle-type', 'aroma'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/bobek',
    provenance: 'spec-sheet',
  },
  {
    id: 'bramling-cross',
    name: 'Bramling Cross',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 8 },
    betaAcidPercent: { min: 2.3, max: 3.5 },
    cohumulonePercentOfAlpha: { min: 30, max: 38 },
    totalOilMlPer100g: { min: 0.7, max: 1.3 },
    oilComposition: {
      myrcenePercent: 30,
      humulenePercent: 28,
      caryophyllenePercent: 9,
      farnesenePercent: 1,
    },
    substitutes: ['east-kent-goldings', 'progress', 'first-gold', 'fuggle'],
    flavorTags: ['blackcurrant', 'lemon', 'spicy', 'dark-fruit'],
    description:
      'A Golding crossed with a wild Manitoban hop, and the most distinctive of the classic English aroma varieties. Late additions give an unmistakable blackcurrant-and-lemon character that suits bitters, porters and winter ales.',
    tags: ['english', 'uk', 'heritage', 'golding-type'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/bramling-cross',
    provenance: 'spec-sheet',
  },
  {
    id: 'brewers-gold',
    name: "Brewer's Gold",
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 4.5, max: 6.5 },
    betaAcidPercent: { min: 2.5, max: 3.5 },
    cohumulonePercentOfAlpha: { min: 36, max: 45 },
    totalOilMlPer100g: { min: 0.8, max: 1.8 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 25,
      caryophyllenePercent: 12.5,
      farnesenePercent: null,
    },
    substitutes: ['bullion', 'northern-brewer', 'galena', 'cluster'],
    flavorTags: ['blackcurrant', 'spicy', 'resinous', 'lemon', 'tea'],
    description:
      "Salmon's 1919 seedling off a wild Manitoban plant, and the root of a startling amount of this catalog — Centennial, Galena, Sterling and Nugget all descend from it. Grown in Germany now rather than Kent, at a modest alpha that makes it a flavor hop: blackcurrant and tea over lemon.",
    tags: ['german', 'heritage', 'wye', 'ancestor'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/brewers-gold',
    provenance: 'spec-sheet',
  },
  {
    id: 'bullion',
    name: 'Bullion',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 5.3, max: 9.5 },
    betaAcidPercent: { min: 4.5, max: 5.3 },
    cohumulonePercentOfAlpha: { min: 47, max: 50 },
    totalOilMlPer100g: { min: 1, max: 1.9 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: null,
      caryophyllenePercent: null,
      farnesenePercent: null,
    },
    substitutes: ['brewers-gold', 'northern-brewer', 'target', 'galena'],
    flavorTags: ['blackcurrant', 'dark-fruit', 'resinous', 'spicy', 'earthy'],
    description:
      "Salmon's other Manitoban seedling, sister to Brewer's Gold and released in 1938 after Institute of Brewing trials. Pungent and old-fashioned in the best way: dark red fruit and blackcurrant over a resinous, earthy bitterness, with a cohumulone near half the alpha that makes that bitterness assertive.",
    tags: ['english', 'uk', 'heritage', 'wye'],
    url: 'https://www.britishhops.org.uk/varieties/bullion/',
    provenance: 'spec-sheet',
  },
  {
    id: 'callista',
    name: 'Callista',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 2, max: 5 },
    betaAcidPercent: { min: 4, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 26 },
    totalOilMlPer100g: { min: 0.7, max: 1.3 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 20,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    substitutes: ['huell-melon', 'mandarina-bavaria', 'hallertau-blanc'],
    flavorTags: ['apricot', 'peach', 'passionfruit', 'stone-fruit'],
    description:
      'A Huell flavor hop with unusually low alpha, bred purely for late and dry-hop work. Delivers ripe stone fruit and passionfruit at dosing rates that would make a bittering hop harsh.',
    tags: ['german', 'hallertau', 'huell', 'flavor-hop'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/callista',
    provenance: 'spec-sheet',
  },
  {
    id: 'challenger',
    name: 'Challenger',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 6.5, max: 9 },
    betaAcidPercent: { min: 3.2, max: 4.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 25 },
    totalOilMlPer100g: { min: 1, max: 1.7 },
    oilComposition: {
      myrcenePercent: 34,
      humulenePercent: 29,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    substitutes: ['northdown', 'target', 'perle', 'admiral'],
    flavorTags: ['spicy', 'cedar', 'green-tea', 'floral'],
    description:
      'The English all-rounder: low enough in cohumulone to bitter cleanly, aromatic enough to carry the late hop in a bitter or porter. Its cedar-and-spice profile is the backbone of countless cask ales.',
    tags: ['english', 'uk', 'wye', 'workhorse'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/wye-challenger',
    provenance: 'spec-sheet',
  },
  {
    id: 'celeia',
    name: 'Celeia',
    origin: 'Slovenia',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3, max: 6 },
    betaAcidPercent: { min: 2, max: 3.5 },
    cohumulonePercentOfAlpha: { min: 26, max: 29 },
    totalOilMlPer100g: { min: 0.6, max: 2.5 },
    oilComposition: {
      myrcenePercent: 28,
      humulenePercent: 22,
      caryophyllenePercent: 8,
      farnesenePercent: 5,
    },
    substitutes: ['styrian-goldings', 'aurora', 'saaz', 'bobek'],
    flavorTags: ['floral', 'spicy', 'lemon', 'noble', 'herbal'],
    description:
      'A Styrian Golding hybrid from Žalec, milder and more lemon-floral than its parent. The everyday Slovenian aroma hop for pale lagers and Belgian ales, and a straight stand-in for Saaz or Styrian Golding when those are dear.',
    tags: ['slovenian', 'ihps', 'styrian-golding-type', 'aroma'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/celeia',
    provenance: 'spec-sheet',
  },
  {
    id: 'east-kent-goldings',
    name: 'East Kent Goldings',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 6.5 },
    betaAcidPercent: { min: 2, max: 3.5 },
    cohumulonePercentOfAlpha: { min: 26, max: 32 },
    totalOilMlPer100g: { min: 0.4, max: 0.9 },
    oilComposition: {
      myrcenePercent: 24,
      humulenePercent: 43,
      caryophyllenePercent: 13,
      farnesenePercent: 0.5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 45,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['fuggle', 'first-gold', 'progress', 'styrian-goldings'],
    flavorTags: ['honey', 'floral', 'earthy', 'lavender', 'marmalade'],
    description:
      'Goldings grown inside the protected East Kent region, and the reference aroma hop for English ale. Humulene-dominant and gently sweet, it wants a late boil or hop-back rather than a heavy dry hop.',
    tags: ['english', 'uk', 'ekg', 'kent', 'protected-origin'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/east-kent-golding',
    provenance: 'spec-sheet',
  },
  {
    id: 'elixir',
    name: 'Elixir',
    origin: 'France',
    growingRegion: 'Alsace',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 5, max: 7 },
    betaAcidPercent: { min: 4.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 25, max: 30 },
    totalOilMlPer100g: { min: 1.8, max: 2.2 },
    oilComposition: {
      myrcenePercent: 72.5,
      humulenePercent: null,
      caryophyllenePercent: null,
      farnesenePercent: null,
    },
    substitutes: ['barbe-rouge', 'triskel', 'strisselspalt', 'aramis'],
    flavorTags: ['orange', 'cognac', 'leather', 'floral', 'rose'],
    description:
      'An Alsace hop that goes somewhere no other French variety does: cognac, leather and tobacco alongside sweet orange and rose. Myrcene runs near three quarters of its oil, which is American-high, and it suits a dark saison or a biere de garde better than it suits a pale one.',
    tags: ['french', 'alsace', 'modern'],
    provenance: 'representative',
  },
  {
    id: 'endeavour',
    name: 'Endeavour',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 3.5, max: 5.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1, max: 1.8 },
    oilComposition: null,
    substitutes: ['challenger', 'cascade', 'jester', 'first-gold'],
    flavorTags: ['blackcurrant', 'grapefruit', 'spicy', 'berry'],
    description:
      'A Wye-bred hedgerow hop with Cascade in its ancestry, sitting between English spice and American citrus. Useful when a traditional bitter needs a little more fruit without leaving the British idiom.',
    tags: ['english', 'uk', 'wye', 'dwarf'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/endeavour',
    provenance: 'representative',
  },
  {
    id: 'ernest',
    name: 'Ernest',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5.3, max: 6.3 },
    betaAcidPercent: { min: 4.5, max: 5 },
    cohumulonePercentOfAlpha: { min: 50, max: 55 },
    totalOilMlPer100g: { min: 0.7, max: 1.1 },
    oilComposition: {
      myrcenePercent: 55,
      humulenePercent: 10,
      caryophyllenePercent: null,
      farnesenePercent: null,
    },
    substitutes: ['fuggle', 'wgv', 'progress', 'east-kent-goldings'],
    flavorTags: ['apricot', 'citrus', 'spicy', 'stone-fruit'],
    description:
      'Named for Ernest Salmon, who bred it, and shelved in its own time: the 1959 trials called its aroma strong, coarse and American, which was a verdict rather than a description. The same apricot and citrus peel is why it has been dug back out, and it reads as a modern hop grown seventy years early.',
    tags: ['english', 'uk', 'heritage', 'wye'],
    url: 'https://www.britishhops.org.uk/varieties/ernest/',
    provenance: 'spec-sheet',
  },
  {
    id: 'first-gold',
    name: 'First Gold',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 6.5, max: 9.5 },
    betaAcidPercent: { min: 2.5, max: 3.5 },
    cohumulonePercentOfAlpha: { min: 30, max: 36 },
    totalOilMlPer100g: { min: 0.7, max: 1.5 },
    oilComposition: {
      myrcenePercent: 26,
      humulenePercent: 34,
      caryophyllenePercent: 11,
      farnesenePercent: 0.5,
    },
    substitutes: ['east-kent-goldings', 'challenger', 'progress', 'fuggle'],
    flavorTags: ['orange-peel', 'floral', 'spicy', 'marmalade'],
    description:
      'The first commercial British dwarf hop, essentially a Golding with more alpha and a firmer citrus edge. Handles the whole hop schedule in a single-hopped bitter or golden ale.',
    tags: ['english', 'uk', 'dwarf', 'wye'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/first-gold',
    provenance: 'spec-sheet',
  },
  {
    id: 'fuggle',
    name: 'Fuggle',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3.5, max: 5.5 },
    betaAcidPercent: { min: 1.5, max: 2.5 },
    cohumulonePercentOfAlpha: { min: 25, max: 32 },
    totalOilMlPer100g: { min: 0.7, max: 1.2 },
    oilComposition: {
      myrcenePercent: 42,
      humulenePercent: 24,
      caryophyllenePercent: 9,
      farnesenePercent: 5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 37,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['willamette', 'east-kent-goldings', 'styrian-goldings', 'progress'],
    flavorTags: ['earthy', 'woody', 'mint', 'grassy'],
    description:
      'The archetypal English earthy hop, and the parent of Willamette and Styrian Golding. Soft, low-alpha and a touch minty, it defines the aroma of traditional bitter, mild and English brown ale.',
    tags: ['english', 'uk', 'heritage', 'landrace'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/fuggles',
    provenance: 'spec-sheet',
  },
  {
    id: 'godiva',
    name: 'Godiva',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 6, max: 10 },
    betaAcidPercent: { min: 3.5, max: 5.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1, max: 1.8 },
    oilComposition: null,
    substitutes: ['jester', 'olicana', 'harlequin', 'cascade'],
    flavorTags: ['tropical', 'citrus', 'chocolate', 'lime', 'berry'],
    description:
      'A Charles Faram hedgerow hop with an odd and useful split: tropical citrus and lime up front, a cocoa-and-berry note underneath. Works late in golden ales and pale IPAs; the chocolate edge is why some brewers also try it in porters.',
    tags: ['english', 'uk', 'charles-faram', 'modern'],
    url: 'https://charlesfaram.com/uk/hop-oracle/godiva/',
    provenance: 'representative',
  },
  {
    id: 'hallertau-blanc',
    name: 'Hallertau Blanc',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 9, max: 12 },
    betaAcidPercent: { min: 3.5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 27 },
    totalOilMlPer100g: { min: 0.8, max: 1.6 },
    oilComposition: {
      myrcenePercent: 48,
      humulenePercent: 14,
      caryophyllenePercent: 7,
      farnesenePercent: 0.5,
    },
    substitutes: ['nelson-sauvin', 'huell-melon', 'mandarina-bavaria', 'callista'],
    flavorTags: ['white-wine', 'gooseberry', 'grape', 'tropical', 'lemongrass'],
    description:
      'A Huell flavor hop bred from Cascade stock that reads as Sauvignon Blanc in the glass. Excellent whirlpooled or dry-hopped into wheat beers, sours and hazy pale ales; too pungent for bittering.',
    tags: ['german', 'hallertau', 'huell', 'flavor-hop'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/hallertau-blanc',
    provenance: 'spec-sheet',
  },
  {
    id: 'hallertauer-mittelfrueh',
    name: 'Hallertauer Mittelfrüh',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3, max: 5.5 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: { min: 18, max: 24 },
    totalOilMlPer100g: { min: 0.7, max: 1.3 },
    oilComposition: {
      myrcenePercent: 24,
      humulenePercent: 48,
      caryophyllenePercent: 12,
      farnesenePercent: 0.5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 46,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['hersbrucker', 'tettnanger', 'spalter', 'liberty'],
    flavorTags: ['noble', 'floral', 'herbal', 'spicy', 'hay'],
    description:
      'The benchmark noble hop: humulene-heavy, low in cohumulone and utterly refined. Carries the aroma of helles, Märzen and Bohemian-influenced pale lagers, and rewards generous late additions.',
    tags: ['german', 'noble', 'hallertau', 'landrace'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/hallertauer-mittelfrueh',
    provenance: 'spec-sheet',
  },
  {
    id: 'hallertauer-tradition',
    name: 'Hallertauer Tradition',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 7 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: { min: 24, max: 30 },
    totalOilMlPer100g: { min: 0.8, max: 1.5 },
    oilComposition: {
      myrcenePercent: 26,
      humulenePercent: 42,
      caryophyllenePercent: 12,
      farnesenePercent: 0.5,
    },
    substitutes: ['hallertauer-mittelfrueh', 'hersbrucker', 'spalter', 'saphir'],
    flavorTags: ['noble', 'floral', 'herbal', 'spicy'],
    description:
      'A wilt-resistant Hüll replacement for Mittelfrüh that reproduces the noble profile with a little more alpha and far better yield. The practical default when a German lager needs classic aroma at scale.',
    tags: ['german', 'noble', 'hallertau', 'huell'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/hallertau-tradition',
    provenance: 'spec-sheet',
  },
  {
    id: 'harlequin',
    name: 'Harlequin',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 8, max: 11 },
    betaAcidPercent: { min: 4, max: 5.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: null,
    substitutes: ['olicana', 'jester', 'mosaic'],
    flavorTags: ['pineapple', 'grapefruit', 'gooseberry', 'tropical'],
    description:
      'A Charles Faram Hop Development Programme release with a thiol-driven tropical profile that is unusual for a British hop. Built for whirlpool and dry-hop work in modern pale ales and IPAs.',
    tags: ['english', 'uk', 'charles-faram', 'modern'],
    url: 'https://charlesfaram.com/uk/hop-oracle/harlequin-hops/',
    provenance: 'representative',
  },
  {
    id: 'herkules',
    name: 'Herkules',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 12, max: 17 },
    betaAcidPercent: { min: 4, max: 5.8 },
    cohumulonePercentOfAlpha: { min: 30, max: 36 },
    totalOilMlPer100g: { min: 1.6, max: 2.6 },
    oilComposition: {
      myrcenePercent: 42,
      humulenePercent: 28,
      caryophyllenePercent: 11,
      farnesenePercent: 0.5,
    },
    substitutes: ['magnum', 'taurus', 'polaris', 'nugget'],
    flavorTags: ['resinous', 'herbal', 'pepper', 'bitter'],
    description:
      'The highest-tonnage bittering hop in Germany, bred at Hüll for alpha yield per hectare. Clean and neutral in the kettle, which is exactly what a pilsner grist wants underneath its noble late additions.',
    tags: ['german', 'hallertau', 'huell', 'high-alpha'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/herkules',
    provenance: 'spec-sheet',
  },
  {
    id: 'hersbrucker',
    name: 'Hersbrucker',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 2, max: 5 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 19, max: 25 },
    totalOilMlPer100g: { min: 0.5, max: 1 },
    oilComposition: {
      myrcenePercent: 25,
      humulenePercent: 30,
      caryophyllenePercent: 11,
      farnesenePercent: 0.5,
    },
    /** Table I's "Hallertauer Hersbrucker"; the domestic-grown row is 50. */
    storageStability: {
      alphaLostPercentSixMonths: 40,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['hallertauer-mittelfrueh', 'spalter', 'tettnanger', 'saaz'],
    flavorTags: ['noble', 'floral', 'hay', 'herbal', 'fruity'],
    description:
      'A soft, slightly fruity noble type from the Hersbruck hills with more beta acid than alpha. Its gentleness suits wheat beers, kellerbier and any lager where the hop should be felt but not tasted.',
    tags: ['german', 'noble', 'hallertau', 'landrace'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/hersbrucker-spaet',
    provenance: 'spec-sheet',
  },
  {
    id: 'huell-melon',
    name: 'Hüll Melon',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 6, max: 8 },
    betaAcidPercent: { min: 6.5, max: 8.5 },
    cohumulonePercentOfAlpha: { min: 17, max: 23 },
    totalOilMlPer100g: { min: 0.6, max: 1.3 },
    oilComposition: {
      myrcenePercent: 38,
      humulenePercent: 20,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    substitutes: ['mandarina-bavaria', 'callista', 'hallertau-blanc', 'cascade'],
    flavorTags: ['honeydew', 'strawberry', 'apricot', 'sweet', 'fruity'],
    description:
      'A Cascade-descended Hüll flavor hop with more beta acid than alpha and an emphatic honeydew-and-strawberry aroma. Reserve it for whirlpool and dry hop, where the fruit survives.',
    tags: ['german', 'hallertau', 'huell', 'flavor-hop'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/huell-melon',
    provenance: 'spec-sheet',
  },
  {
    id: 'jester',
    name: 'Jester',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1, max: 1.8 },
    oilComposition: null,
    substitutes: ['olicana', 'harlequin', 'cascade', 'bramling-cross'],
    flavorTags: ['grapefruit', 'blackcurrant', 'tropical', 'lychee'],
    description:
      'A Charles Faram hedgerow selection that puts New World grapefruit and blackcurrant on an English plant. Works as a single late hop in golden ales and British-grown IPAs.',
    tags: ['english', 'uk', 'charles-faram', 'modern'],
    url: 'https://charlesfaram.com/uk/hop-oracle/jester/',
    provenance: 'representative',
  },
  {
    id: 'kazbek',
    name: 'Kazbek',
    origin: 'Czech Republic',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 8 },
    betaAcidPercent: { min: 3.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 22, max: 28 },
    totalOilMlPer100g: { min: 1, max: 1.7 },
    oilComposition: {
      myrcenePercent: 38,
      humulenePercent: 24,
      caryophyllenePercent: 10,
      farnesenePercent: 3,
    },
    substitutes: ['saaz', 'sladek', 'premiant', 'lublin'],
    flavorTags: ['lemon', 'citrus', 'herbal', 'spicy'],
    description:
      'A Žatec release descended from wild Russian germplasm, best described as Saaz with a lemon-peel top note. Excellent late in Czech pale lagers and in pilsners that want a little brightness.',
    tags: ['czech', 'zatec', 'saaz-type'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/kazbek',
    provenance: 'spec-sheet',
  },
  {
    id: 'lublin',
    name: 'Lublin',
    origin: 'Poland',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3, max: 5 },
    betaAcidPercent: { min: 2, max: 4 },
    cohumulonePercentOfAlpha: { min: 22, max: 28 },
    totalOilMlPer100g: { min: 0.5, max: 1.2 },
    oilComposition: {
      myrcenePercent: 32,
      humulenePercent: 28,
      caryophyllenePercent: 10,
      farnesenePercent: 6,
    },
    substitutes: ['saaz', 'hersbrucker', 'tettnanger', 'hallertauer-mittelfrueh'],
    flavorTags: ['noble', 'herbal', 'spicy', 'grassy'],
    description:
      'The Polish Saaz-type landrace, known locally as Lubelski, and the aroma behind Polish pale lagers. Low alpha and quietly herbal, it is a straight substitute for Saaz when supply is tight.',
    tags: ['polish', 'noble', 'saaz-type', 'lubelski'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/lublin',
    provenance: 'spec-sheet',
  },
  {
    id: 'magnum',
    name: 'Magnum',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 11, max: 16 },
    betaAcidPercent: { min: 4.5, max: 7 },
    cohumulonePercentOfAlpha: { min: 21, max: 29 },
    totalOilMlPer100g: { min: 1.6, max: 2.6 },
    oilComposition: {
      myrcenePercent: 38,
      humulenePercent: 32,
      caryophyllenePercent: 10,
      farnesenePercent: 0.5,
    },
    substitutes: ['herkules', 'taurus', 'nugget', 'warrior'],
    flavorTags: ['clean', 'resinous', 'herbal', 'pepper'],
    description:
      'The default clean bittering hop of German brewing: high alpha, moderate cohumulone and almost no flavor carry-over. Add it at 60 minutes and let the aroma hops do the talking.',
    tags: ['german', 'hallertau', 'huell', 'high-alpha', 'clean-bittering'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/hallertauer-magnum',
    provenance: 'spec-sheet',
  },
  {
    id: 'mandarina-bavaria',
    name: 'Mandarina Bavaria',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 22, max: 28 },
    totalOilMlPer100g: { min: 1.4, max: 2.5 },
    oilComposition: {
      myrcenePercent: 60,
      humulenePercent: 11,
      caryophyllenePercent: 6,
      farnesenePercent: 0.5,
    },
    substitutes: ['huell-melon', 'callista', 'cascade', 'hallertau-blanc'],
    flavorTags: ['tangerine', 'citrus', 'orange-peel', 'sweet'],
    description:
      'A Cascade daughter from Hüll with a myrcene-dominated oil and an unmistakable tangerine aroma. Best whirlpooled or dry-hopped into wheat beers, pale ales and German-hopped IPAs.',
    tags: ['german', 'hallertau', 'huell', 'flavor-hop'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/mandarina-bavaria',
    provenance: 'spec-sheet',
  },
  {
    id: 'marynka',
    name: 'Marynka',
    origin: 'Poland',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9, max: 12 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 25, max: 32 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: null,
    substitutes: ['lublin', 'magnum', 'northdown', 'target'],
    flavorTags: ['resinous', 'herbal', 'earthy', 'spicy'],
    description:
      'The Polish workhorse dual-purpose variety, with enough alpha to bitter a lager outright and a robust herbal-resinous aroma late. A useful, inexpensive stand-in for English bittering hops.',
    tags: ['polish', 'dual-purpose'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/marynka',
    provenance: 'representative',
  },
  {
    id: 'mistral',
    name: 'Mistral',
    origin: 'France',
    growingRegion: 'Alsace',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 6.5, max: 8.5 },
    betaAcidPercent: { min: 3.1, max: 3.8 },
    cohumulonePercentOfAlpha: { min: 29, max: 39 },
    totalOilMlPer100g: { min: 1, max: 1.5 },
    oilComposition: {
      myrcenePercent: 62,
      humulenePercent: 11.15,
      caryophyllenePercent: null,
      farnesenePercent: null,
    },
    substitutes: ['barbe-rouge', 'elixir', 'triskel', 'strisselspalt'],
    flavorTags: ['orange', 'melon', 'rose', 'lychee'],
    description:
      "Comptoir Agricole's 2016 release and the most modern-tasting hop France grows: orange and melon with rose and lychee behind them. Myrcene runs around 60 % of the oil, which is why it reads closer to a New World hop than to Strisselspalt.",
    tags: ['french', 'alsace', 'modern', 'comptoir-agricole'],
    provenance: 'representative',
  },
  {
    id: 'monroe',
    name: 'Monroe',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 2.8, max: 2.9 },
    betaAcidPercent: { min: 6.5, max: 7.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0.6, max: 0.95 },
    oilComposition: {
      myrcenePercent: 37,
      humulenePercent: null,
      caryophyllenePercent: null,
      farnesenePercent: null,
    },
    substitutes: ['hersbrucker', 'saphir', 'hallertauer-mittelfrueh'],
    flavorTags: ['cherry', 'orange', 'raspberry'],
    description:
      'Bred in Germany off a US wild hop, and unusual twice over: beta acid runs at more than double the alpha, and what it tastes of is morello cherry and raspberry rather than anything noble. Too little alpha to bitter with, which is not what it is for.',
    tags: ['german', 'aroma', 'low-alpha'],
    provenance: 'representative',
  },
  {
    id: 'most',
    name: 'Most',
    origin: 'Czech Republic',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 25, max: 32 },
    totalOilMlPer100g: { min: 1, max: 1 },
    oilComposition: {
      myrcenePercent: 32.5,
      humulenePercent: 23,
      caryophyllenePercent: null,
      farnesenePercent: 0.5,
    },
    substitutes: ['jester', 'premiant', 'sladek', 'kazbek'],
    flavorTags: ['strawberry', 'apricot', 'grapefruit', 'spicy'],
    description:
      'A Charles Faram cross grown with Czech growers, and a sister to Jester: strawberry and apricot over grapefruit and a Saazer spice. It is the modern British breeding program applied to Bohemian stock, and it tastes like both parents.',
    tags: ['czech', 'modern', 'charles-faram'],
    provenance: 'representative',
  },
  {
    id: 'mystic',
    name: 'Mystic',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 7 },
    betaAcidPercent: { min: 2, max: 4 },
    cohumulonePercentOfAlpha: { min: 30, max: 40 },
    totalOilMlPer100g: { min: 0.5, max: 1.5 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 35,
      caryophyllenePercent: null,
      farnesenePercent: 1,
    },
    substitutes: ['jester', 'olicana', 'harlequin', 'archer'],
    flavorTags: ['passionfruit', 'blackcurrant', 'citrus'],
    description:
      'A daughter of Jester released in 2017, carrying the same passionfruit and blackcurrant at a gentler alpha. Myrcene and humulene sit level in the oil, which is what keeps the tropical note from running away from the beer.',
    tags: ['english', 'uk', 'modern', 'charles-faram'],
    provenance: 'representative',
  },
  {
    id: 'northdown',
    name: 'Northdown',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 4.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 26, max: 32 },
    totalOilMlPer100g: { min: 1, max: 1.8 },
    oilComposition: {
      myrcenePercent: 30,
      humulenePercent: 35,
      caryophyllenePercent: 12,
      farnesenePercent: 0.5,
    },
    substitutes: ['challenger', 'target', 'admiral', 'progress'],
    flavorTags: ['earthy', 'spicy', 'pine', 'floral'],
    description:
      'A Northern Brewer seedling that bitters cleanly and finishes with a dark, resinous English spice. A traditional choice for porters, stouts and strong bitters.',
    tags: ['english', 'uk', 'wye', 'dual-purpose'],
    url: 'https://charlesfaram.com/uk/hop-oracle/northdown/',
    provenance: 'spec-sheet',
  },
  {
    id: 'northern-brewer',
    name: 'Northern Brewer',
    origin: 'Germany',
    growingRegion: 'Hallertau',
    cultivar: 'northern-brewer',
    purpose: 'dual',
    alphaAcidPercent: { min: 6, max: 10 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: { min: 20, max: 30 },
    totalOilMlPer100g: { min: 1.5, max: 2 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 25,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    /** Table I's "Hallertauer Northern Brewer", the German-grown row. */
    storageStability: {
      alphaLostPercentSixMonths: 25,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['us-northern-brewer', 'perle', 'challenger', 'northdown'],
    flavorTags: ['woody', 'mint', 'pine', 'earthy', 'herbal'],
    description:
      'Bred at Wye and grown commercially in the Hallertau: mint, wood and a clean pine resin that defined steam beer and a generation of German ales. Dual-purpose in the old sense — it bitters firmly and still leaves a recognizable late-hop character.',
    tags: ['german', 'hallertau', 'wye', 'dual-purpose', 'classic'],
    url: 'https://charlesfaram.com/uk/hop-oracle/northern-brewer/',
    provenance: 'spec-sheet',
  },
  {
    id: 'olicana',
    name: 'Olicana',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 3.5, max: 5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: null,
    substitutes: ['jester', 'harlequin', 'citra', 'mosaic'],
    flavorTags: ['mango', 'passionfruit', 'grapefruit', 'elderflower', 'tropical'],
    description:
      'The most tropical of the Charles Faram hedgerow varieties, layering mango and passionfruit over an elderflower note. Reserved almost entirely for whirlpool and dry hop in modern British pale ales.',
    tags: ['english', 'uk', 'charles-faram', 'modern'],
    url: 'https://charlesfaram.com/uk/hop-oracle/olicana-hops/',
    provenance: 'representative',
  },
  {
    id: 'opal',
    name: 'Opal',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 5, max: 10 },
    betaAcidPercent: { min: 3.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 12, max: 18 },
    totalOilMlPer100g: { min: 0.8, max: 1.8 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 25,
      caryophyllenePercent: 11,
      farnesenePercent: 0.5,
    },
    substitutes: ['saphir', 'spalter', 'hallertauer-tradition', 'perle'],
    flavorTags: ['spicy', 'aniseed', 'citrus', 'herbal'],
    description:
      'A Hüll special-flavor hop with strikingly low cohumulone, so it bitters far more softly than its alpha suggests. Late additions bring an aniseed-and-lemon spice that suits saisons and hoppy wheat beers.',
    tags: ['german', 'hallertau', 'huell', 'special-flavor'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/opal',
    provenance: 'spec-sheet',
  },
  {
    id: 'perle',
    name: 'Perle',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 6, max: 9.5 },
    betaAcidPercent: { min: 2.5, max: 4.5 },
    cohumulonePercentOfAlpha: { min: 27, max: 33 },
    totalOilMlPer100g: { min: 0.5, max: 1.5 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 33,
      caryophyllenePercent: 10,
      farnesenePercent: 0.5,
    },
    substitutes: ['hallertauer-tradition', 'northdown', 'challenger', 'spalter'],
    flavorTags: ['spicy', 'mint', 'floral', 'herbal'],
    description:
      'A Hallertauer-derived dual hop that bitters reliably and finishes with a slightly minty German spice. The standard middle-of-the-boil hop for altbier, kölsch and everyday German ales.',
    tags: ['german', 'hallertau', 'huell', 'dual-purpose'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/perle',
    provenance: 'spec-sheet',
  },
  {
    id: 'phoenix',
    name: 'Phoenix',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 8, max: 12 },
    betaAcidPercent: { min: 4, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 24, max: 30 },
    totalOilMlPer100g: { min: 1.2, max: 2.5 },
    oilComposition: null,
    substitutes: ['challenger', 'northdown', 'target', 'admiral'],
    flavorTags: ['spicy', 'chocolate', 'molasses', 'pine', 'floral'],
    description:
      'A wilt-tolerant Wye replacement for Challenger, with a darker spice — chocolate, molasses and pine — than its parent. Handles the whole hop schedule in a bitter, porter or stout, and stores better than most English aroma hops.',
    tags: ['english', 'uk', 'wye', 'dual-purpose'],
    url: 'https://charlesfaram.com/uk/hop-oracle/phoenix/',
    provenance: 'representative',
  },
  {
    id: 'pilgrim',
    name: 'Pilgrim',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9, max: 13 },
    betaAcidPercent: { min: 4, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 32, max: 38 },
    totalOilMlPer100g: { min: 1.2, max: 2.2 },
    oilComposition: null,
    substitutes: ['target', 'admiral', 'northdown', 'challenger'],
    flavorTags: ['spicy', 'lemon', 'herbal', 'resinous'],
    description:
      'A high-alpha English dwarf hop bred for yield and storage stability, with a lemony spice when used late. Bitters a bitter without the coarse edge some UK high-alpha varieties bring.',
    tags: ['english', 'uk', 'dwarf', 'wye'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/pilgrim',
    provenance: 'representative',
  },
  {
    id: 'polaris',
    name: 'Polaris',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 18, max: 23 },
    betaAcidPercent: { min: 4.5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 26 },
    totalOilMlPer100g: { min: 3.5, max: 5 },
    oilComposition: {
      myrcenePercent: 55,
      humulenePercent: 15,
      caryophyllenePercent: 7,
      farnesenePercent: 0.5,
    },
    substitutes: ['herkules', 'magnum', 'taurus', 'summit'],
    flavorTags: ['mint', 'menthol', 'pine', 'eucalyptus'],
    description:
      'The highest-alpha variety grown in Germany and, at up to 5 mL of oil per 100 g, one of the most aromatic hops grown anywhere. The menthol-pine character is overwhelming in quantity — treat late additions as a seasoning measured in grams.',
    tags: ['german', 'hallertau', 'huell', 'high-alpha', 'special-flavor'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/polaris',
    provenance: 'spec-sheet',
  },
  {
    id: 'premiant',
    name: 'Premiant',
    origin: 'Czech Republic',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 4, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 18, max: 24 },
    totalOilMlPer100g: { min: 1, max: 1.8 },
    oilComposition: {
      myrcenePercent: 38,
      humulenePercent: 28,
      caryophyllenePercent: 10,
      farnesenePercent: 2,
    },
    substitutes: ['saaz', 'sladek', 'kazbek', 'hallertauer-tradition'],
    flavorTags: ['herbal', 'spicy', 'floral', 'noble'],
    description:
      'A Czech dual-purpose hop with Saaz-like low cohumulone and roughly twice the alpha. It bitters a pilsner smoothly and still gives a recognizably Bohemian aroma late in the boil.',
    tags: ['czech', 'zatec', 'dual-purpose'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/premiant',
    provenance: 'spec-sheet',
  },
  {
    id: 'progress',
    name: 'Progress',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 7.5 },
    betaAcidPercent: { min: 2, max: 3.2 },
    cohumulonePercentOfAlpha: { min: 28, max: 34 },
    totalOilMlPer100g: { min: 0.8, max: 1.5 },
    oilComposition: {
      myrcenePercent: 30,
      humulenePercent: 34,
      caryophyllenePercent: 11,
      farnesenePercent: 0.5,
    },
    substitutes: ['fuggle', 'east-kent-goldings', 'first-gold', 'bramling-cross'],
    flavorTags: ['earthy', 'honey', 'herbal', 'soft-fruit'],
    description:
      'Bred as a wilt-tolerant Fuggle replacement, slightly sweeter and fuller than its parent. A dependable English aroma hop for milds, bitters and brown ales.',
    tags: ['english', 'uk', 'wye', 'fuggle-type'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/progress',
    provenance: 'spec-sheet',
  },
  {
    id: 'saaz',
    name: 'Saaz',
    origin: 'Czech Republic',
    growingRegion: 'Žatec',
    cultivar: 'saaz',
    purpose: 'aroma',
    alphaAcidPercent: { min: 2.5, max: 4.5 },
    betaAcidPercent: { min: 3, max: 4.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 26 },
    totalOilMlPer100g: { min: 0.4, max: 1 },
    oilComposition: {
      myrcenePercent: 28,
      humulenePercent: 25,
      caryophyllenePercent: 8,
      farnesenePercent: 16,
    },
    /** Table I's "Saazer (Czech)". */
    storageStability: {
      alphaLostPercentSixMonths: 50,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['sladek', 'lublin', 'hersbrucker', 'premiant'],
    flavorTags: ['noble', 'herbal', 'spicy', 'floral', 'earthy'],
    description:
      'The Žatec landrace that defines Bohemian pilsner: low alpha, low cohumulone and an oil profile with far more farnesene than any other noble hop. Use it generously and late; its aroma fades quickly in storage. The same plant is grown in Germany, Washington and Michigan — those lots are cataloged separately, because terroir moves both the alpha and the farnesene that make Žatec Saaz itself.',
    tags: ['czech', 'noble', 'zatec', 'landrace', 'zatecky-cervenak'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/saazer',
    provenance: 'spec-sheet',
  },
  {
    id: 'saaz-germany',
    name: 'Saaz (Germany)',
    origin: 'Germany',
    growingRegion: 'Hallertau',
    cultivar: 'saaz',
    purpose: 'aroma',
    alphaAcidPercent: { min: 2.5, max: 5 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: { min: 23, max: 28 },
    totalOilMlPer100g: { min: 0.4, max: 1 },
    oilComposition: {
      myrcenePercent: 32,
      humulenePercent: 22,
      caryophyllenePercent: 8,
      farnesenePercent: 12,
    },
    substitutes: ['saaz', 'hersbrucker', 'tettnanger', 'sladek'],
    flavorTags: ['noble', 'herbal', 'spicy', 'floral', 'earthy'],
    description:
      'The Saaz landrace grown in the Hallertau, sometimes sold as Saazer. It keeps the herbal-spicy noble frame and a high farnesene fraction, but German lots usually run a touch more alpha and a shade less of the classic Žatec earth. The continental pick when the bag actually says Hallertau, not Žatec.',
    tags: ['german', 'hallertau', 'saaz', 'noble', 'landrace', 'saazer'],
    url: 'https://www.hopfenforschung.de/en/sorte/deutscher-saazer/',
    provenance: 'spec-sheet',
  },
  {
    id: 'saaz-late',
    name: 'Saaz Late',
    origin: 'Czech Republic',
    growingRegion: 'Žatec',
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3.5, max: 6 },
    betaAcidPercent: { min: 4, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 25 },
    totalOilMlPer100g: { min: 0.5, max: 1.2 },
    oilComposition: {
      myrcenePercent: 30,
      humulenePercent: 24,
      caryophyllenePercent: 8,
      farnesenePercent: 12,
    },
    substitutes: ['saaz', 'sladek', 'kazbek', 'lublin'],
    flavorTags: ['noble', 'herbal', 'spicy', 'floral', 'earthy'],
    description:
      'A Žatec selection bred to ripen later than the landrace and hold a little more alpha, without leaving the Saaz idiom. Same farnesene-led herbal spice, slightly fuller; the practical choice when classic Saaz is short or you want a touch more bittering from the aroma charge.',
    tags: ['czech', 'zatec', 'saaz-type', 'noble'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/saaz-late',
    provenance: 'spec-sheet',
  },
  {
    id: 'saphir',
    name: 'Saphir',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 2, max: 5 },
    betaAcidPercent: { min: 4, max: 7 },
    cohumulonePercentOfAlpha: { min: 12, max: 18 },
    totalOilMlPer100g: { min: 0.8, max: 1.5 },
    oilComposition: {
      myrcenePercent: 30,
      humulenePercent: 22,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    substitutes: ['hallertauer-mittelfrueh', 'opal', 'spalter', 'hersbrucker'],
    flavorTags: ['citrus', 'tangerine', 'floral', 'noble'],
    description:
      'A Hüll aroma hop with very low cohumulone and a bright citrus lift on top of a noble base. Suits helles, wheat beers and any lager that would benefit from a hint of tangerine.',
    tags: ['german', 'hallertau', 'huell', 'spalt-type'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/saphir',
    provenance: 'spec-sheet',
  },
  {
    id: 'sladek',
    name: 'Sládek',
    origin: 'Czech Republic',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4.5, max: 8 },
    betaAcidPercent: { min: 6, max: 9 },
    cohumulonePercentOfAlpha: { min: 22, max: 28 },
    totalOilMlPer100g: { min: 1, max: 1.8 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 22,
      caryophyllenePercent: 9,
      farnesenePercent: 1,
    },
    substitutes: ['saaz', 'premiant', 'kazbek', 'hersbrucker'],
    flavorTags: ['herbal', 'spicy', 'grassy', 'fruity'],
    description:
      'A Saaz descendant with markedly more beta acid than alpha, giving a soft, long-keeping bitterness. Late additions bring a fuller, slightly fruitier version of the Bohemian aroma.',
    tags: ['czech', 'zatec', 'saaz-type'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/sladek',
    provenance: 'spec-sheet',
  },
  {
    id: 'smaragd',
    name: 'Smaragd',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 6 },
    betaAcidPercent: { min: 3.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 13, max: 18 },
    totalOilMlPer100g: { min: 0.4, max: 0.8 },
    oilComposition: {
      myrcenePercent: 30,
      humulenePercent: 35,
      caryophyllenePercent: 11,
      farnesenePercent: 0.5,
    },
    substitutes: ['saphir', 'opal', 'hallertauer-tradition', 'spalter-select'],
    flavorTags: ['floral', 'spicy', 'fruity', 'noble', 'herbal'],
    description:
      'Also sold as Emerald, a Hüll special-flavor hop with very low cohumulone and a gentle floral-spicy fruit note. Low oil, so it wants generous late additions in helles, wheat beer and hoppy German ales rather than a token whirlpool charge.',
    tags: ['german', 'hallertau', 'huell', 'special-flavor', 'emerald'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/smaragd',
    provenance: 'spec-sheet',
  },
  {
    id: 'sovereign',
    name: 'Sovereign',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4.5, max: 6.5 },
    betaAcidPercent: { min: 2.1, max: 3.1 },
    cohumulonePercentOfAlpha: { min: 26, max: 30 },
    totalOilMlPer100g: { min: 0.6, max: 1 },
    oilComposition: {
      myrcenePercent: 27.5,
      humulenePercent: 23.5,
      caryophyllenePercent: null,
      farnesenePercent: 4,
    },
    substitutes: ['fuggle', 'east-kent-goldings', 'progress', 'first-gold'],
    flavorTags: ['floral', 'grassy', 'herbal', 'mint'],
    description:
      'A granddaughter of Pioneer released in 2006, and the quiet one of the modern English set: flowers and cut grass with a herbal, almost minty edge, and a green-tea bitterness that stays soft. Single-hops a bitter without ever raising its voice.',
    tags: ['english', 'uk', 'wye', 'hedgerow'],
    url: 'https://www.britishhops.org.uk/varieties/sovereign/',
    provenance: 'spec-sheet',
  },
  {
    id: 'spalter',
    name: 'Spalter',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 2.5, max: 5.5 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: { min: 22, max: 29 },
    totalOilMlPer100g: { min: 0.5, max: 1 },
    oilComposition: {
      myrcenePercent: 22,
      humulenePercent: 28,
      caryophyllenePercent: 10,
      farnesenePercent: 12,
    },
    /** Table I's "German Spalter"; the domestic-grown row is 50. */
    storageStability: {
      alphaLostPercentSixMonths: 45,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['saaz', 'hersbrucker', 'tettnanger', 'hallertauer-mittelfrueh'],
    flavorTags: ['noble', 'spicy', 'floral', 'herbal'],
    description:
      'The landrace of the small Spalt growing region, a Saaz-type with real farnesene and a fine, dry spice. Traditional in Franconian lagers and a natural fit for kölsch and altbier.',
    tags: ['german', 'noble', 'spalt', 'landrace'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/spalt-spalter',
    provenance: 'spec-sheet',
  },
  {
    id: 'spalter-select',
    name: 'Spalter Select',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3, max: 6.5 },
    betaAcidPercent: { min: 2.5, max: 5 },
    cohumulonePercentOfAlpha: { min: 20, max: 28 },
    totalOilMlPer100g: { min: 0.5, max: 1.2 },
    oilComposition: {
      myrcenePercent: 22,
      humulenePercent: 20,
      caryophyllenePercent: 8,
      farnesenePercent: 12,
    },
    substitutes: ['spalter', 'tettnanger', 'saaz', 'smaragd'],
    flavorTags: ['noble', 'spicy', 'floral', 'herbal', 'earthy'],
    description:
      'A Hüll wilt-resistant replacement for the Spalt landrace, keeping the farnesene-led dry spice with a little more alpha and far better yield. The practical noble hop for Franconian lagers, kölsch and altbier when genuine Spalter is scarce.',
    tags: ['german', 'noble', 'spalt', 'huell', 'spalt-type'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/spalter-select',
    provenance: 'spec-sheet',
  },
  {
    id: 'strisselspalt',
    name: 'Strisselspalt',
    origin: 'France',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3, max: 5.5 },
    betaAcidPercent: { min: 3, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 25 },
    totalOilMlPer100g: { min: 0.6, max: 1.1 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 22,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    /** Table I's "Strisselspalt (France)". */
    storageStability: {
      alphaLostPercentSixMonths: 35,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['hersbrucker', 'aramis', 'hallertauer-mittelfrueh', 'tettnanger'],
    flavorTags: ['floral', 'herbal', 'pear', 'hay'],
    description:
      'The noble-type landrace of Alsace, comparable to Hersbrucker but with a lighter, more floral and faintly pear-like aroma. The classic late hop for bière de garde and French-influenced lagers.',
    tags: ['french', 'noble', 'alsace', 'landrace'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/strisselspalt',
    provenance: 'spec-sheet',
  },
  {
    id: 'styrian-cardinal',
    name: 'Styrian Cardinal',
    origin: 'Slovenia',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 11, max: 15 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.5, max: 2.8 },
    oilComposition: null,
    substitutes: ['styrian-wolf', 'ariana', 'citra', 'mosaic'],
    flavorTags: ['tropical', 'citrus', 'pine', 'berry', 'resinous'],
    description:
      'A modern IHPS high-alpha with tropical citrus and pine rather than the earthy Styrian Golding profile. Bitters cleanly enough for a Slovenian IPA and, late, gives a fruit-and-resin lift that sits between Wolf and a New World dual hop.',
    tags: ['slovenian', 'ihps', 'modern', 'high-alpha', 'dual-purpose'],
    provenance: 'representative',
  },
  {
    id: 'styrian-goldings',
    name: 'Styrian Goldings',
    origin: 'Slovenia',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3.5, max: 6 },
    betaAcidPercent: { min: 2, max: 3.5 },
    cohumulonePercentOfAlpha: { min: 25, max: 31 },
    totalOilMlPer100g: { min: 0.5, max: 1.2 },
    oilComposition: {
      myrcenePercent: 30,
      humulenePercent: 32,
      caryophyllenePercent: 10,
      farnesenePercent: 4,
    },
    storageStability: {
      alphaLostPercentSixMonths: 37,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['fuggle', 'east-kent-goldings', 'willamette', 'progress'],
    flavorTags: ['earthy', 'herbal', 'lemon', 'floral'],
    description:
      'Despite the name this is a Slovenian Fuggle selection, not a Golding — earthier and a touch more lemony than its English parent. A staple late hop in Belgian ales as well as British bitters.',
    tags: ['slovenian', 'savinjski-golding', 'fuggle-type'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/styrian-savinjski-golding',
    provenance: 'spec-sheet',
  },
  {
    id: 'styrian-wolf',
    name: 'Styrian Wolf',
    origin: 'Slovenia',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 17 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 2, max: 3.5 },
    oilComposition: null,
    substitutes: ['styrian-goldings', 'magnum', 'nugget', 'columbus'],
    flavorTags: ['tropical', 'citrus', 'pine', 'resinous'],
    description:
      'A modern high-alpha release from the Slovenian Institute of Hop Research, unusually aromatic for its bittering strength. Bitters hard and, late, gives a tropical-citrus lift that suits Slovenian-hopped IPAs.',
    tags: ['slovenian', 'ihps', 'modern', 'high-alpha'],
    url: 'https://charlesfaram.com/uk/hop-oracle/styrian-wolf/',
    provenance: 'representative',
  },
  {
    id: 'sussex',
    name: 'Sussex',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4.3, max: 5.8 },
    betaAcidPercent: { min: 2.4, max: 3.2 },
    cohumulonePercentOfAlpha: { min: 29, max: 32 },
    totalOilMlPer100g: { min: 0.4, max: 0.6 },
    oilComposition: {
      myrcenePercent: 42,
      humulenePercent: 23,
      caryophyllenePercent: null,
      farnesenePercent: null,
    },
    substitutes: ['fuggle', 'wgv', 'progress'],
    flavorTags: ['earthy', 'grassy', 'mint', 'herbal'],
    description:
      'Not bred but found: a chance seedling in a hedgerow at Gate Court in Northiam, spotted in 2005, whose oil analysis matched nothing already known. It drinks close to a Fuggle — earthy and grassy with a cool minty lift — and holds its flavor well for a hop with this little oil.',
    tags: ['english', 'uk', 'hedgerow', 'heritage'],
    url: 'https://www.britishhops.org.uk/varieties/sussex/',
    provenance: 'spec-sheet',
  },
  {
    id: 'tango',
    name: 'Tango',
    origin: 'Germany',
    growingRegion: 'Hallertau',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 7.5, max: 12.4 },
    betaAcidPercent: { min: 6, max: 10 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 2.4, max: 4 },
    oilComposition: {
      myrcenePercent: 29.5,
      humulenePercent: null,
      caryophyllenePercent: null,
      farnesenePercent: 5,
    },
    substitutes: ['cascade', 'hallertauer-tradition', 'mandarina-bavaria', 'callista'],
    flavorTags: ['passionfruit', 'grapefruit', 'resinous', 'lemon'],
    description:
      'A Hull release grown in the Hallertau since 2020, out of US Cascade crossed onto a Hallertauer Tradition line, and bred as much for holding up to a warming climate as for what it tastes of. Passionfruit and grapefruit over a resinous base, finishing on lemon and apple.',
    tags: ['german', 'hallertau', 'huell', 'modern'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/tango',
    provenance: 'representative',
  },
  {
    id: 'target',
    name: 'Target',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 9.5, max: 12.5 },
    betaAcidPercent: { min: 4.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 29, max: 36 },
    totalOilMlPer100g: { min: 1.4, max: 2.5 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 18,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    substitutes: ['admiral', 'northdown', 'challenger', 'pilgrim'],
    flavorTags: ['earthy', 'sage', 'resinous', 'citrus'],
    description:
      'The dominant British high-alpha hop, giving a robust, faintly sage-like bitterness. Standard in cask bitters and stouts, and increasingly used late for a distinctly English marmalade note.',
    tags: ['english', 'uk', 'wye', 'high-alpha'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/wye-target',
    provenance: 'spec-sheet',
  },
  {
    id: 'taurus',
    name: 'Taurus',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 12, max: 17 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 20, max: 26 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 35,
      caryophyllenePercent: 12,
      farnesenePercent: 0.5,
    },
    substitutes: ['magnum', 'herkules', 'polaris', 'nugget'],
    flavorTags: ['clean', 'herbal', 'spicy', 'floral'],
    description:
      'A Hallertauer Magnum sibling with lower cohumulone and an unusually humulene-rich oil for a high-alpha hop. Bitters cleanly and leaves a faint noble spice if any is held back for the whirlpool.',
    tags: ['german', 'hallertau', 'huell', 'high-alpha'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/hallertauer-taurus',
    provenance: 'spec-sheet',
  },
  {
    id: 'tettnanger',
    name: 'Tettnanger',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 2.5, max: 5.5 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: { min: 22, max: 28 },
    totalOilMlPer100g: { min: 0.5, max: 1.1 },
    oilComposition: {
      myrcenePercent: 25,
      humulenePercent: 26,
      caryophyllenePercent: 8,
      farnesenePercent: 12,
    },
    /** Table I's "Tettnang Tettnanger", the German-grown row. */
    storageStability: {
      alphaLostPercentSixMonths: 42,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['hallertauer-mittelfrueh', 'spalter', 'saaz', 'us-tettnanger'],
    flavorTags: ['noble', 'spicy', 'floral', 'herbal'],
    description:
      'The Saaz-type landrace of the Tettnang region near Lake Constance, finer and more floral than the Hallertau nobles. Do not confuse it with US Tettnanger, which is a different plant entirely.',
    tags: ['german', 'noble', 'tettnang', 'landrace', 'saaz-type'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/tettnang-tettnanger',
    provenance: 'spec-sheet',
  },
  {
    id: 'triskel',
    name: 'Triskel',
    origin: 'France',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 2.8, max: 5 },
    betaAcidPercent: { min: 4, max: 5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.5, max: 2 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 11,
      caryophyllenePercent: 6.25,
      farnesenePercent: 0.5,
    },
    substitutes: ['strisselspalt', 'aramis', 'barbe-rouge', 'hersbrucker'],
    flavorTags: ['floral', 'citrus', 'peach', 'herbal'],
    description:
      "Strisselspalt crossed with an English Yeoman male, and the most useful of the modern Alsace hops: it keeps Strisselspalt's delicacy but adds sweet citrus and stone fruit over it. Beta acid runs above alpha, so it is a late hop for saison and biere de garde rather than a bittering charge.",
    tags: ['french', 'alsace', 'aroma', 'comptoir-agricole'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/triskel',
    provenance: 'spec-sheet',
  },
  {
    id: 'uk-golding',
    name: 'UK Golding',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 7 },
    betaAcidPercent: { min: 2, max: 3 },
    cohumulonePercentOfAlpha: { min: 25, max: 28 },
    totalOilMlPer100g: { min: 0.4, max: 1 },
    oilComposition: {
      myrcenePercent: 30,
      humulenePercent: 39.5,
      caryophyllenePercent: 14.5,
      farnesenePercent: null,
    },
    substitutes: ['east-kent-goldings', 'us-goldings', 'progress', 'first-gold'],
    flavorTags: ['floral', 'woody', 'spicy', 'citrus'],
    description:
      'Golding grown outside the Kent parishes that earn the "East Kent" name, and genetically alongside it rather than apart from it. The same floral, woody, faintly spicy finish, and the usual reason to choose it is price and availability rather than character.',
    tags: ['english', 'uk', 'goldings-type', 'heritage'],
    provenance: 'representative',
  },
  {
    id: 'wgv',
    name: 'WGV',
    origin: 'United Kingdom',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 7.5 },
    betaAcidPercent: { min: 2.5, max: 3.5 },
    cohumulonePercentOfAlpha: { min: 37, max: 37 },
    totalOilMlPer100g: { min: 0.8, max: 1.2 },
    oilComposition: {
      myrcenePercent: 25.5,
      humulenePercent: 40,
      caryophyllenePercent: null,
      farnesenePercent: 1.5,
    },
    substitutes: ['east-kent-goldings', 'fuggle', 'progress', 'first-gold'],
    flavorTags: ['floral', 'herbal', 'spicy', 'earthy'],
    description:
      'Whitbread Golding Variety, selected in a Kent garden in 1911 and planted commercially from 1953. The name promises Goldings and the farnesene says Fuggle; what arrives is sweet flowers over earth and herbal spice, with the mild clean bitterness a traditional ale wants.',
    tags: ['english', 'uk', 'heritage', 'whitbread'],
    url: 'https://www.britishhops.org.uk/varieties/wgv/',
    provenance: 'spec-sheet',
  },
];
